/**
 * lib/webPush — Web Push subscription helpers.
 *
 * Flusso:
 *  1. Verifica supporto browser (serviceWorker + PushManager)
 *  2. Registra il service worker (se non già registrato)
 *  3. Chiede permesso Notifiche
 *  4. Sottoscrive via PushManager.subscribe() con la VAPID public key del backend
 *  5. Invia la subscription al backend via PUT /api/push/subscription
 *
 * `unsubscribePush()` fa il contrario: unsubscribe + notifica backend.
 */
import api from "@/lib/api";

function base64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function isPushSupported() {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

export function isIOSNonStandalone() {
  if (typeof navigator === "undefined") return false;
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const standalone = window.matchMedia?.("(display-mode: standalone)")?.matches
    || navigator.standalone;
  return isIOS && !standalone;
}

export async function enablePush() {
  if (!isPushSupported()) throw new Error("Web Push non supportato da questo browser");
  if (isIOSNonStandalone()) {
    throw new Error("Su iPhone/iPad devi prima installare l'app sulla schermata Home (Condividi → Aggiungi a Home)");
  }

  // 1. Registra SW (se non già)
  let reg = await navigator.serviceWorker.getRegistration("/");
  if (!reg) {
    reg = await navigator.serviceWorker.register("/service-worker.js", { scope: "/" });
  }
  // Attendi che sia active
  if (reg.installing) {
    await new Promise((resolve) => {
      reg.installing.addEventListener("statechange", (e) => {
        if (e.target.state === "activated") resolve();
      });
    });
  }

  // 2. Permesso notifiche (deve partire da un gesto utente)
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Permesso notifiche negato");

  // 3. Recupera public key VAPID
  const { data: keyData } = await api.get("/push/public-key");
  if (!keyData?.publicKey) throw new Error("Web Push non configurato sul server");

  // 4. Sottoscrivi (riusa quella esistente se già presente)
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64ToUint8Array(keyData.publicKey),
    });
  }

  // 5. Invia al backend
  const json = sub.toJSON();
  await api.put("/push/subscription", {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    user_agent: navigator.userAgent,
  });

  return { ok: true, endpoint: json.endpoint };
}

export async function disablePush() {
  if (!isPushSupported()) return { ok: true, was_subscribed: false };
  const reg = await navigator.serviceWorker.getRegistration("/");
  if (!reg) return { ok: true, was_subscribed: false };
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { ok: true, was_subscribed: false };
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  try {
    await api.delete("/push/subscription", { data: { endpoint } });
  } catch (_) { /* server sync best-effort */ }
  return { ok: true, was_subscribed: true };
}

export async function getPushSubscriptionStatus() {
  if (!isPushSupported()) return { supported: false, subscribed: false, permission: "denied" };
  const permission = typeof Notification !== "undefined" ? Notification.permission : "denied";
  const reg = await navigator.serviceWorker.getRegistration("/");
  if (!reg) return { supported: true, subscribed: false, permission };
  const sub = await reg.pushManager.getSubscription();
  return { supported: true, subscribed: !!sub, permission };
}
